import { useEffect, useState } from "react";
import { GasPump, TrendUp, TrendDown, Warning } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Live fuel-cost estimate for a load being created.
 * Reads current diesel $/gal from `/api/dispatch/fuel-price` (cached 24h from EIA)
 * and the user's saved MPG from `/api/settings/pricing`.
 * Multiplies by miles ÷ MPG, and if rate is set shows margin impact.
 *
 * Props: `miles`, `rate` from parent form state. `mpg` is fetched from prefs
 * (falls back to 6.5 Class-8 default when the user hasn't customized it).
 * Auto-hides when miles is empty. Fully client-side calc.
 */
export default function FuelCostEstimateCard({ miles, rate }) {
  const [fuel, setFuel] = useState(null);
  const [userMpg, setUserMpg] = useState(6.5);
  const [loading, setLoading] = useState(true);

  // Fetch diesel price + user's saved MPG in parallel. Both are server-cached
  // (24h) or stable, so one call per session is plenty.
  useEffect(() => {
    let alive = true;
    Promise.all([
      client.get("/dispatch/fuel-price").catch(() => null),
      client.get("/settings/pricing").catch(() => null),
    ]).then(([fuelR, prefsR]) => {
      if (!alive) return;
      setFuel(fuelR?.data || null);
      const savedMpg = Number(prefsR?.data?.mpg);
      if (savedMpg && !Number.isNaN(savedMpg) && savedMpg > 0) {
        setUserMpg(savedMpg);
      }
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const milesNum = Number(miles) || 0;
  const rateNum = Number(rate) || 0;
  const mpgUsed = Math.max(0.1, userMpg);

  // Hide the card when there's nothing useful to compute
  if (milesNum <= 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="fuel-cost-loading">
        <div className="text-xs text-neutral-500">Loading current diesel price…</div>
      </div>
    );
  }

  const dieselPrice = fuel?.price_per_gallon || 4.15;
  const gallons = milesNum / mpgUsed;
  const fuelCost = gallons * dieselPrice;

  // If the pilot entered a rate, we can preview net-of-fuel and $/mi
  const hasRate = rateNum > 0;
  const netAfterFuel = hasRate ? rateNum - fuelCost : null;
  const marginPct = hasRate ? (netAfterFuel / rateNum) * 100 : null;

  // Color-code the margin state: healthy ≥ 40%, tight 20-40%, negative < 20%
  const marginTone =
    marginPct === null ? "text-neutral-400" :
    marginPct >= 40 ? "text-emerald-400" :
    marginPct >= 20 ? "text-[#FF5A1F]" :
    "text-red-400";

  const wowUp = (fuel?.wow_change_usd || 0) > 0.005;
  const wowDown = (fuel?.wow_change_usd || 0) < -0.005;
  const wowIcon = wowUp ? <TrendUp size={11} className="text-amber-400" /> :
                  wowDown ? <TrendDown size={11} className="text-emerald-400" /> : null;

  return (
    <div
      className="rounded-lg border border-[#FF5A1F]/20 bg-gradient-to-br from-[#FF5A1F]/5 to-transparent p-4"
      data-testid="fuel-cost-estimate-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <GasPump size={16} weight="fill" className="text-[#FF5A1F]" />
        <div className="overline text-[#FF5A1F]">Fuel cost estimate</div>
        {fuel?.source === "fallback" && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-400">
            <Warning size={10} weight="fill" /> Stale price
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric
          label="Diesel"
          value={`$${dieselPrice.toFixed(3)}/gal`}
          sub={
            <span className="inline-flex items-center gap-0.5">
              {wowIcon}
              {fuel?.source === "eia" ? "EIA weekly" : fuel?.source === "cache" ? "cached" : "fallback"}
            </span>
          }
          testid="fuel-price-metric"
        />
        <Metric
          label="Gallons"
          value={gallons.toFixed(1)}
          sub={`${milesNum.toLocaleString()}mi @ ${mpgUsed} MPG`}
          testid="fuel-gallons-metric"
        />
        <Metric
          label="Fuel cost"
          value={`$${fuelCost.toFixed(0)}`}
          sub={`${(fuelCost / milesNum).toFixed(2)}/mi`}
          testid="fuel-cost-metric"
          accent="text-[#FF5A1F]"
        />
        {hasRate ? (
          <Metric
            label="Net after fuel"
            value={netAfterFuel >= 0 ? `$${Math.round(netAfterFuel).toLocaleString()}` : `-$${Math.round(-netAfterFuel).toLocaleString()}`}
            sub={`${marginPct.toFixed(0)}% margin`}
            accent={marginTone}
            testid="fuel-net-metric"
          />
        ) : (
          <Metric
            label="Add rate"
            value="—"
            sub="See net margin"
            accent="text-neutral-500"
            testid="fuel-net-empty"
          />
        )}
      </div>

      <div className="text-[10px] text-neutral-500 mt-3 leading-relaxed">
        Estimate only. Assumes {mpgUsed} MPG loaded average. Set your truck's MPG in <a href="/app/settings/pricing" className="text-[#FF5A1F] hover:underline">Settings → Pricing</a>.
      </div>
    </div>
  );
}

function Metric({ label, value, sub, accent = "text-white", testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`font-display text-lg font-semibold mt-0.5 tabular-nums ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-600 mt-0.5">{sub}</div>}
    </div>
  );
}
